import React, { useState, useContext } from 'react';
import {
    Box,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    useTheme,
    useMediaQuery
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CloseIcon from '@mui/icons-material/Close';

import { StateContext } from '../context/StateProvider';
import NotificacionesService from '../../services/notificaciones.service';

/**
 * Componente para activar/desactivar notificaciones push
 */
export default function NotificationToggle({ compacto = false }) {
    const { state, updateState } = useContext(StateContext);
    const [showModalPermiso, setShowModalPermiso] = useState(false);
    const [mensajeError, setMensajeError] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

    const desactivarNotificaciones = async () => {
        if (!('serviceWorker' in navigator)) return;

        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return;

        const sub = await registration.pushManager.getSubscription();

        if (sub) {
            await sub.unsubscribe();
        }

        updateState({ notificacionesActivas: false });
    };

    const toggleNotificaciones = async () => {
        if (state.notificacionesActivas) {
            await desactivarNotificaciones();
            return;
        }

        try {
            await NotificacionesService.subscribe();
            updateState({ notificacionesActivas: true });
        } catch (error) {
            console.error('Error al activar notificaciones:', error);
            // Mostrar modal si hay error con permisos
            setShowModalPermiso(true);
            setMensajeError(error.message || 'No se pudieron activar las notificaciones');
        }
    };

    const cerrarModalPermiso = () => {
        setShowModalPermiso(false);
        setMensajeError('');
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                    size="small"
                    title={state.notificacionesActivas ? 'Desactivar notificaciones' : 'Activar notificaciones'}
                    onClick={toggleNotificaciones}
                    sx={compacto ? { p: 0.5 } : undefined}
                >
                    {state.notificacionesActivas ? (
                        <NotificationsIcon sx={{ fontSize: compacto ? 19 : 24, color: '#cc071e' }} />
                    ) : (
                        <NotificationsOffIcon sx={{ fontSize: compacto ? 19 : 24, color: '#6c757d' }} />
                    )}
                </IconButton>
            </Box>

            {/* Modal de permisos de notificaciones */}
            <Dialog
                open={showModalPermiso}
                onClose={cerrarModalPermiso}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 2,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        m: isMobile ? 0 : 2,
                    }
                }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                        <Box
                            sx={{
                                width: { xs: 36, sm: 40 },
                                height: { xs: 36, sm: 40 },
                                borderRadius: "50%",
                                backgroundColor: "#fee",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <NotificationsOffIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: "#212529",
                                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            }}
                        >
                            Notificaciones bloqueadas
                        </Typography>
                    </Box>
                    <IconButton onClick={cerrarModalPermiso} size="small" sx={{ color: "#6c757d" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Typography sx={{ mb: 1 }}>No se pudieron activar las notificaciones.</Typography>
                    <Typography sx={{ mb: 0 }}>
                        <strong>Motivo:</strong> {mensajeError}
                    </Typography>
                    {mensajeError.includes('denegado') && (
                        <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Para activar las notificaciones:
                            </Typography>
                            <Typography variant="body2" component="ol" sx={{ mb: 0, pl: 2.5 }}>
                                <li>Haz clic en el icono de candado/información en la barra de direcciones</li>
                                <li>Busca la opción "Notificaciones"</li>
                                <li>Selecciona "Permitir"</li>
                                <li>Recarga la página e intenta de nuevo</li>
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="contained"
                        onClick={cerrarModalPermiso}
                        sx={{
                            backgroundColor: "#cc071e",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: "#a30618",
                            },
                        }}
                    >
                        Entendido
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
